import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AddParticipantPayload,
  ApproveMarginPayload,
  AttachCompetitorPayload,
  CancelPayload,
  CreateInteractionPayload,
  CreateOportunidadePayload,
  ListOportunidadesParams,
  LosePayload,
  MoveStagePayload,
  OPORTUNIDADES_KEYS,
  ProposeOpportunityPayload,
  ReactivatePayload,
  SuspendPayload,
  TransferPayload,
  UpdateCompetitorLinkPayload,
  UpdateOportunidadePayload,
  UploadAttachmentMeta,
  WinPayload,
  adicionarParticipante,
  aprovarMargem,
  atualizarConcorrente,
  atualizarOportunidade,
  buscarIndicadorConversao,
  buscarIndicadorFechamentoVencido,
  buscarIndicadorForecastMensal,
  buscarIndicadorPipeline,
  buscarIndicadorPorEtapa,
  buscarIndicadorSemProximaAcao,
  buscarIndicadorStalled,
  buscarOportunidade,
  cancelarOportunidade,
  criarInteracao,
  criarOportunidade,
  desvincularConcorrente,
  enviarAnexo,
  ganharOportunidade,
  listarAnexos,
  listarConcorrentes,
  listarEtapas,
  listarHistorico,
  listarInteracoes,
  listarMotivosPerda,
  listarOportunidades,
  listarOrigens,
  listarPapeisParticipante,
  listarParticipantes,
  listarTipos,
  moverEtapa,
  perderOportunidade,
  proporOportunidade,
  reativarOportunidade,
  removerAnexo,
  removerInteracao,
  removerParticipante,
  salvarRascunho,
  suspenderOportunidade,
  transferirOportunidade,
  vincularConcorrente,
} from '../api';

/**
 * React Query hooks de Oportunidades Comerciais. Fatia 9: leitura
 * (list, detail, stages, indicadores). Mutations entram junto com o
 * formulário nas fatias seguintes.
 */

export { OPORTUNIDADES_KEYS };

export function useOportunidades(params?: ListOportunidadesParams) {
  return useQuery({
    queryKey: OPORTUNIDADES_KEYS.list(params),
    queryFn: () => listarOportunidades(params),
  });
}

export function useOportunidade(id: string | undefined) {
  return useQuery({
    queryKey: id ? OPORTUNIDADES_KEYS.detail(id) : ['oportunidades', 'detail', 'noop'],
    queryFn: () => buscarOportunidade(id as string),
    enabled: !!id,
  });
}

export function useEtapas(includeInactive = false) {
  return useQuery({
    queryKey: [...OPORTUNIDADES_KEYS.stages(), includeInactive],
    queryFn: () => listarEtapas(includeInactive),
    // Etapas mudam raramente — cachear por 5 min reduz round-trips do
    // formulário e do Kanban.
    staleTime: 5 * 60 * 1000,
  });
}

export function useIndicadorPipeline() {
  return useQuery({
    queryKey: OPORTUNIDADES_KEYS.indicator('pipeline'),
    queryFn: buscarIndicadorPipeline,
  });
}

export function useIndicadorForecastMensal(params: {
  from?: string;
  to?: string;
  weighted?: boolean;
}) {
  return useQuery({
    queryKey: OPORTUNIDADES_KEYS.indicator('forecast-monthly', params),
    queryFn: () => buscarIndicadorForecastMensal(params),
  });
}

export function useIndicadorConversao(params: { from?: string; to?: string }) {
  return useQuery({
    queryKey: OPORTUNIDADES_KEYS.indicator('conversion-rate', params),
    queryFn: () => buscarIndicadorConversao(params),
  });
}

export function useIndicadorPorEtapa() {
  return useQuery({
    queryKey: OPORTUNIDADES_KEYS.indicator('by-stage'),
    queryFn: buscarIndicadorPorEtapa,
  });
}

export function useIndicadorStalled() {
  return useQuery({
    queryKey: OPORTUNIDADES_KEYS.indicator('stalled'),
    queryFn: buscarIndicadorStalled,
  });
}

export function useIndicadorSemProximaAcao() {
  return useQuery({
    queryKey: OPORTUNIDADES_KEYS.indicator('without-next-action'),
    queryFn: buscarIndicadorSemProximaAcao,
  });
}

export function useIndicadorFechamentoVencido() {
  return useQuery({
    queryKey: OPORTUNIDADES_KEYS.indicator('expected-closing-overdue'),
    queryFn: buscarIndicadorFechamentoVencido,
  });
}

// -------- Domínios administráveis (5min de cache — mudam raramente) --

export function useOrigens() {
  return useQuery({
    queryKey: [...OPORTUNIDADES_KEYS.all, 'origens'],
    queryFn: listarOrigens,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTipos() {
  return useQuery({
    queryKey: [...OPORTUNIDADES_KEYS.all, 'tipos'],
    queryFn: listarTipos,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMotivosPerda() {
  return useQuery({
    queryKey: [...OPORTUNIDADES_KEYS.all, 'motivos-perda'],
    queryFn: listarMotivosPerda,
    staleTime: 5 * 60 * 1000,
  });
}

// -------- Mutations --------------------------------------------------

export function useCriarOportunidade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOportunidadePayload) => criarOportunidade(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPORTUNIDADES_KEYS.all });
    },
  });
}

export function useSalvarRascunho() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOportunidadePayload) => salvarRascunho(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPORTUNIDADES_KEYS.all });
    },
  });
}

export function useAtualizarOportunidade(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateOportunidadePayload) => atualizarOportunidade(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPORTUNIDADES_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.all, 'list'] });
    },
  });
}

/**
 * Factory de mutations de ação (win/lose/suspend/reactivate/cancel/
 * transfer/approve-margin) — todas invalidam detalhe + lista. Extraído
 * para evitar 7 hooks quase idênticos.
 */
function useAcaoMutation<P>(
  id: string,
  fn: (id: string, payload: P) => Promise<unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: P) => fn(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPORTUNIDADES_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.all, 'list'] });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.all, 'indicators'] });
    },
  });
}

export const useGanharOportunidade = (id: string) =>
  useAcaoMutation<WinPayload>(id, ganharOportunidade);
export const usePerderOportunidade = (id: string) =>
  useAcaoMutation<LosePayload>(id, perderOportunidade);
export const useSuspenderOportunidade = (id: string) =>
  useAcaoMutation<SuspendPayload>(id, suspenderOportunidade);
export const useReativarOportunidade = (id: string) =>
  useAcaoMutation<ReactivatePayload>(id, reativarOportunidade);
export const useCancelarOportunidade = (id: string) =>
  useAcaoMutation<CancelPayload>(id, cancelarOportunidade);
export const useTransferirOportunidade = (id: string) =>
  useAcaoMutation<TransferPayload>(id, transferirOportunidade);
export const useAprovarMargem = (id: string) =>
  useAcaoMutation<ApproveMarginPayload>(id, aprovarMargem);

/**
 * Move oportunidade entre etapas. O Kanban aplica atualização otimista
 * na cache antes de mandar o POST; em caso de erro (etapa obrigatória
 * pulada, margem sem aprovação para "Proposta", justificativa curta em
 * retrocesso…), o handler reverte a mudança na cache.
 *
 * Diferente das demais mutations, NÃO usamos `useAcaoMutation` porque
 * o Kanban gerencia optimistic updates manualmente e prefere controlar
 * a invalidação depois da rollback.
 */
// -------- Timeline (change_history) e Interações --------------------

export function useHistorico(id: string | undefined, params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: id
      ? [...OPORTUNIDADES_KEYS.detail(id), 'history', params]
      : ['oportunidades', 'history', 'noop'],
    queryFn: () => listarHistorico(id as string, params),
    enabled: !!id,
  });
}

export function useInteracoes(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? [...OPORTUNIDADES_KEYS.detail(id), 'interactions']
      : ['oportunidades', 'interactions', 'noop'],
    queryFn: () => listarInteracoes(id as string),
    enabled: !!id,
  });
}

export function useCriarInteracao(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateInteractionPayload) => criarInteracao(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'interactions'] });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'history'] });
      qc.invalidateQueries({ queryKey: OPORTUNIDADES_KEYS.detail(id) });
    },
  });
}

export function useRemoverInteracao(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (interactionId: string) => removerInteracao(id, interactionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'interactions'] });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'history'] });
    },
  });
}

// -------- Participantes ---------------------------------------------

export function usePapeisParticipante() {
  return useQuery({
    queryKey: [...OPORTUNIDADES_KEYS.all, 'participant-roles'],
    queryFn: listarPapeisParticipante,
    staleTime: 5 * 60 * 1000,
  });
}

export function useParticipantes(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? [...OPORTUNIDADES_KEYS.detail(id), 'participants']
      : ['oportunidades', 'participants', 'noop'],
    queryFn: () => listarParticipantes(id as string),
    enabled: !!id,
  });
}

export function useAdicionarParticipante(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddParticipantPayload) => adicionarParticipante(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'participants'] });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'history'] });
    },
  });
}

export function useRemoverParticipante(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (participantId: string) => removerParticipante(id, participantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'participants'] });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'history'] });
    },
  });
}

// -------- Concorrentes ----------------------------------------------

export function useConcorrentes(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? [...OPORTUNIDADES_KEYS.detail(id), 'competitors']
      : ['oportunidades', 'competitors', 'noop'],
    queryFn: () => listarConcorrentes(id as string),
    enabled: !!id,
  });
}

export function useVincularConcorrente(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AttachCompetitorPayload) => vincularConcorrente(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'competitors'] });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'history'] });
    },
  });
}

export function useAtualizarConcorrente(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, payload }: { linkId: string; payload: UpdateCompetitorLinkPayload }) =>
      atualizarConcorrente(id, linkId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'competitors'] });
    },
  });
}

export function useDesvincularConcorrente(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => desvincularConcorrente(id, linkId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'competitors'] });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'history'] });
    },
  });
}

// -------- Anexos ----------------------------------------------------

export function useAnexos(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? [...OPORTUNIDADES_KEYS.detail(id), 'attachments']
      : ['oportunidades', 'attachments', 'noop'],
    queryFn: () => listarAnexos(id as string),
    enabled: !!id,
  });
}

export function useEnviarAnexo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, meta }: { file: File; meta: UploadAttachmentMeta }) =>
      enviarAnexo(id, file, meta),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'attachments'] });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'history'] });
    },
  });
}

export function useRemoverAnexo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => removerAnexo(id, attachmentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'attachments'] });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'history'] });
    },
  });
}

export function useMoverEtapa(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: MoveStagePayload) => moverEtapa(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPORTUNIDADES_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.all, 'list'] });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.all, 'indicators'] });
    },
  });
}

/** Conversão da oportunidade em proposta (RN-COM-018/019). */
export function useProporOportunidade(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProposeOpportunityPayload = {}) => proporOportunidade(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPORTUNIDADES_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.detail(id), 'history'] });
    },
  });
}
